import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import financeApiService from "../../../../services/financeApiService";
import financeV2Adapters from "../../../../adapters/financeV2Adapters";
import ConfirmModal from "../../../common/ConfirmModal/ConfirmModal";
import styles from "../FinanceDetailV2.module.scss";

// LS-722 — suivi des demandes de solde anticipé par véhicule du dossier :
// demande envoyée au financeur → facture pro forma reçue → soldé/payé.
// Actions 100 % manuelles (décision actée le 10/09/2026), l'envoi de l'email
// de demande au financeur est exclu de cette V1. Après un solde partiel, le
// financeur renvoie un échéancier recalculé : sa ressaisie (mensualités
// retenues) fait partie du process — d'où le rappel affiché sous le tableau.

const STATUT_SOLDE_LABELS = {
  demande_envoyee: "Demande envoyée",
  facture_recue: "Facture reçue",
  solde: "Soldé",
};

const aujourdhui = () => new Date().toISOString().slice(0, 10);

// État d'édition local d'une ligne de solde (inputs contrôlés), reconstruit à
// chaque réponse API — chaque réponse renvoie le suivi complet.
const editsDepuisSoldes = (soldes) =>
  Object.fromEntries(
    soldes.map((s) => [
      s.immatriculation,
      {
        montant: s.montant ?? "",
        dateFacture: s.dateFacture || "",
        datePaiement: s.datePaiement || "",
      },
    ]),
  );

const SoldesSection = ({ demandeUuid, disabled }) => {
  const [suivi, setSuivi] = useState({
    immatriculationsDisponibles: [],
    soldes: [],
  });
  const [edits, setEdits] = useState({});
  const [selection, setSelection] = useState([]);
  const [dateDemande, setDateDemande] = useState(aujourdhui());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [immatAAnnuler, setImmatAAnnuler] = useState(null);

  useEffect(() => {
    if (!demandeUuid) return;
    setIsLoading(true);
    financeApiService
      .listerSoldes(demandeUuid)
      .then((res) => appliquerSuivi(res.data))
      .catch(() => toast.error("Impossible de charger le suivi des soldes."))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demandeUuid]);

  function appliquerSuivi(body) {
    const data = financeV2Adapters.fromApi.suiviSoldes(body);
    setSuivi(data);
    setEdits(editsDepuisSoldes(data.soldes));
    setSelection([]);
  }

  const soldeParImmat = Object.fromEntries(
    suivi.soldes.map((s) => [s.immatriculation, s]),
  );

  // Immatriculations du dossier sans demande de solde : candidates à la sélection.
  const immatsSansSolde = suivi.immatriculationsDisponibles.filter(
    (immat) => !soldeParImmat[immat],
  );

  function toggleSelection(immat) {
    setSelection((prev) =>
      prev.includes(immat)
        ? prev.filter((i) => i !== immat)
        : [...prev, immat],
    );
  }

  async function handleDemanderSolde() {
    if (selection.length === 0 || !dateDemande) return;
    setIsSaving(true);
    try {
      const res = await financeApiService.enregistrerDemandeDeSolde(
        demandeUuid,
        selection,
        dateDemande,
      );
      appliquerSuivi(res.data);
      toast.success("Demande de solde enregistrée.");
    } catch {
      toast.error("L'enregistrement de la demande de solde a échoué.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleEnregistrerSolde(immat) {
    setIsSaving(true);
    try {
      const res = await financeApiService.mettreAJourSolde(
        demandeUuid,
        immat,
        edits[immat] || {},
      );
      appliquerSuivi(res.data);
      toast.success("Suivi du solde mis à jour.");
    } catch {
      toast.error("La mise à jour du solde a échoué.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAnnulerSolde() {
    const immat = immatAAnnuler;
    setIsSaving(true);
    try {
      const res = await financeApiService.annulerSolde(demandeUuid, immat);
      appliquerSuivi(res.data);
      toast.success("Demande de solde annulée.");
    } catch {
      toast.error("L'annulation de la demande de solde a échoué.");
    } finally {
      setIsSaving(false);
      setImmatAAnnuler(null);
    }
  }

  function updateEdit(immat, champ, valeur) {
    setEdits((prev) => ({
      ...prev,
      [immat]: { ...prev[immat], [champ]: valeur },
    }));
  }

  if (isLoading) {
    return <span className={styles.financierCardStatusMuted}>Chargement…</span>;
  }

  if (suivi.immatriculationsDisponibles.length === 0) {
    return (
      <span className={styles.financierCardStatusMuted}>
        Aucun véhicule immatriculé rattaché au dossier (les contrats ne sont
        peut-être pas encore générés).
      </span>
    );
  }

  return (
    <div>
      <div className={styles.echeancierRecapTableWrapper}>
        <table className={styles.echeancierRecapTable}>
          <thead>
            <tr>
              <th>Immatriculation</th>
              <th>Statut</th>
              <th>Demandé le</th>
              <th>Montant du solde</th>
              <th>Facture du</th>
              <th>Payé le</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {suivi.immatriculationsDisponibles.map((immat) => {
              const solde = soldeParImmat[immat];
              const edit = edits[immat] || {};

              if (!solde) {
                return (
                  <tr key={immat}>
                    <td>
                      <label style={{ margin: 0, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={selection.includes(immat)}
                          onChange={() => toggleSelection(immat)}
                          disabled={disabled || isSaving}
                          style={{ marginRight: 6 }}
                        />
                        {immat}
                      </label>
                    </td>
                    <td colSpan={5}>—</td>
                    <td />
                  </tr>
                );
              }

              return (
                <tr key={immat}>
                  <td>{immat}</td>
                  <td>{STATUT_SOLDE_LABELS[solde.statut] || solde.statut}</td>
                  <td>{solde.dateDemande || "—"}</td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={styles.periodInputEditable}
                      placeholder="Montant €"
                      aria-label={`Montant du solde ${immat}`}
                      value={edit.montant}
                      onChange={(e) =>
                        updateEdit(immat, "montant", e.target.value)
                      }
                      disabled={disabled || isSaving}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      className={styles.periodInputEditable}
                      aria-label={`Date de facture ${immat}`}
                      value={edit.dateFacture}
                      onChange={(e) =>
                        updateEdit(immat, "dateFacture", e.target.value)
                      }
                      disabled={disabled || isSaving}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      className={styles.periodInputEditable}
                      aria-label={`Date de paiement ${immat}`}
                      value={edit.datePaiement}
                      onChange={(e) =>
                        updateEdit(immat, "datePaiement", e.target.value)
                      }
                      disabled={disabled || isSaving}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className={styles.echeancierSaveBtn}
                      onClick={() => handleEnregistrerSolde(immat)}
                      disabled={disabled || isSaving}
                      title={`Enregistrer le suivi ${immat}`}
                    >
                      <i className="fa fa-check" />
                    </button>
                    <button
                      type="button"
                      className={styles.echeancierCancelBtn}
                      onClick={() => setImmatAAnnuler(immat)}
                      disabled={disabled || isSaving}
                      title={`Annuler la demande de solde ${immat}`}
                    >
                      <i className="fa fa-trash" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {immatsSansSolde.length > 0 && !disabled && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
          <label
            className={styles.searchLabel}
            style={{ margin: 0 }}
            htmlFor="soldes-date-demande"
          >
            Date de la demande
          </label>
          <input
            id="soldes-date-demande"
            type="date"
            className={styles.periodInputEditable}
            value={dateDemande}
            onChange={(e) => setDateDemande(e.target.value)}
            disabled={isSaving}
          />
          <button
            type="button"
            className={styles.echeancierSaveBtn}
            onClick={handleDemanderSolde}
            disabled={isSaving || selection.length === 0 || !dateDemande}
          >
            {/* fa-check et non fa-paper-plane : l'action ne fait qu'enregistrer
                le suivi, aucun email n'est envoyé au financeur (V1 actée 10/09). */}
            <i className="fa fa-check mr-1" />
            Demander le solde ({selection.length} véhicule
            {selection.length > 1 ? "s" : ""})
          </button>
        </div>
      )}

      <div className={styles.financierCardStatusMuted} style={{ marginTop: 8 }}>
        Après un solde partiel, ressaisissez l&apos;échéancier recalculé par le
        financeur (mensualités retenues) dans les périodes du devis.
      </div>

      <ConfirmModal
        isOpen={!!immatAAnnuler}
        title="Annuler la demande de solde"
        message={`Voulez-vous vraiment annuler la demande de solde de « ${immatAAnnuler} » ? Son suivi (facture, paiement) sera supprimé.`}
        confirmLabel="Annuler la demande"
        isLoading={isSaving}
        onConfirm={handleAnnulerSolde}
        onCancel={() => setImmatAAnnuler(null)}
      />
    </div>
  );
};

export default SoldesSection;
